import { Avatar } from '@atlas/ui-primitives'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { type BrandResult, brandInitials, marketSummary } from './brand-catalog'

export interface BrandResultCardProps {
	brand: BrandResult
	/** Geography code carried into the brand page — market or region — or `null` to open it unscoped. */
	market: string | null
}

/**
 * A brand in the Explore results grid - the page's one destination, so it reads as a
 * link rather than a filter: monogram, name, rolled-up asset count, and the markets it
 * covers.
 *
 * A real `Link`, not a button: the brand page is a URL, and middle- or cmd-clicking a
 * result to open it alongside the list is how anyone compares two brands.
 */
export function BrandResultCard({ brand, market }: BrandResultCardProps) {
	const query = market ? `?market=${encodeURIComponent(market)}` : ''
	const markets = marketSummary(brand.markets)

	return (
		<Link
			to={`/explore/brand/${brand.brandId}${query}`}
			className="group flex items-start gap-3 rounded-ds-card border border-ds-border bg-ds-surface-elevated p-4 transition-all hover:border-ds-text hover:shadow-ds-md"
		>
			<Avatar aria-hidden fallback={brandInitials(brand.name)} size="md" className="shrink-0" />
			<div className="min-w-0 flex-1">
				<span className="block truncate font-semibold leading-snug text-ds-text">{brand.name}</span>
				<span className="mt-1 block font-mono text-xs text-ds-text-muted">
					{brand.assetCount} {brand.assetCount === 1 ? 'asset' : 'assets'}
				</span>
				{markets === '' ? null : (
					<span className="mt-2 block truncate text-xs text-ds-text-subtle" title={brand.markets.join(' · ')}>
						{markets}
					</span>
				)}
			</div>
			<ArrowRight
				aria-hidden
				className="mt-0.5 h-4 w-4 shrink-0 text-ds-text-subtle transition-transform group-hover:translate-x-0.5 group-hover:text-ds-text"
			/>
		</Link>
	)
}
